/**
 * useLiveSkillsFromOpenClaw — Hook to fetch skills directly from OpenClaw filesystem
 * 
 * - Fetches GET /machines/:id/openclaw-live-skills
 * - Auto-refetch every 30s
 * - Loading/error states
 * - Graceful degradation when runner offline
 */

import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { api } from '../api/client';

export interface LiveSkill {
  name: string;
  description: string;
  category: string;
  emoji: string;
  version: string;
  tags: string[];
  installed: true;
}

export interface LiveSkillsData {
  skills: LiveSkill[];
  syncedAt: string;
  runnerStatus: 'online' | 'offline' | 'fallback';
  source: 'openclaw' | 'database';
  machineId: string;
}

export interface UseLiveSkillsResult {
  skills: LiveSkill[];
  loading: boolean;
  error: string;
  syncedAt: string;
  runnerStatus: 'online' | 'offline' | 'fallback' | 'loading';
  source: 'openclaw' | 'database' | '';
  refetch: () => Promise<void>;
  uninstallSkill: (skillName: string) => Promise<boolean>;
}

const REFETCH_INTERVAL = 30_000; // 30 seconds

export function useLiveSkillsFromOpenClaw(machineId: string): UseLiveSkillsResult {
  const [skills, setSkills] = useState<LiveSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncedAt, setSyncedAt] = useState('');
  const [runnerStatus, setRunnerStatus] = useState<'online' | 'offline' | 'fallback' | 'loading'>('loading');
  const [source, setSource] = useState<'openclaw' | 'database' | ''>('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSkills = useCallback(async () => {
    try {
      const res = await api.get<{ data: LiveSkillsData }>(`/machines/${machineId}/openclaw-live-skills`);
      const data = res?.data || res as any;
      
      setSkills(data.skills || []);
      setSyncedAt(data.syncedAt || new Date().toISOString());
      setRunnerStatus(data.runnerStatus || 'online');
      setSource(data.source || 'openclaw');
      setError('');
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar skills do OpenClaw');
      setRunnerStatus('offline');
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  const uninstallSkill = useCallback(async (skillName: string): Promise<boolean> => {
    try {
      await api.delete(`/machines/${machineId}/openclaw-live-skills/${skillName}`);
      // Optimistic update
      setSkills(prev => prev.filter(s => s.name !== skillName));
      setSyncedAt(new Date().toISOString());
      return true;
    } catch (e: any) {
      setError(e.message || 'Erro ao desinstalar skill');
      return false;
    }
  }, [machineId]);

  // Initial fetch
  useEffect(() => {
    setLoading(true);
    fetchSkills();
  }, [machineId, fetchSkills]);

  // Auto-refetch every 30s
  useEffect(() => {
    intervalRef.current = setInterval(fetchSkills, REFETCH_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchSkills]);

  return {
    skills,
    loading,
    error,
    syncedAt,
    runnerStatus,
    source,
    refetch: fetchSkills,
    uninstallSkill,
  };
}
